import {DateAdapter, type MatDateFormats} from '@angular/material/core';
import dayjs, {Dayjs, isDayjs} from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import objectSupport from 'dayjs/plugin/objectSupport';

dayjs.extend(localeData);
dayjs.extend(objectSupport);
dayjs.extend(customParseFormat);

export class DayJSAdapter extends DateAdapter<Dayjs> {
  override getYear(date: Dayjs): number {
    return date.year();
  }
  override getMonth(date: Dayjs): number {
    return date.month();
  }
  override getDate(date: Dayjs): number {
    return date.date();
  }
  override getDayOfWeek(date: Dayjs): number {
    return date.day();
  }
  override getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    switch (style) {
      case 'long':
        return dayjs.months();
      case 'short':
        return dayjs.monthsShort();
      case 'narrow':
        return dayjs.monthsShort().map(m => m[0]);
    }
  }
  override getDateNames(): string[] {
    return Array(31)
      .fill('_')
      .map((_, i) => (i + 1).toLocaleString());
  }
  override getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    switch (style) {
      case 'long':
        return dayjs.weekdays();
      case 'short':
        return dayjs.weekdaysShort();
      case 'narrow':
        return dayjs.weekdaysMin();
    }
  }
  override getYearName(date: Dayjs): string {
    return date.format('YYYY');
  }
  override getFirstDayOfWeek(): number {
    return 0;
  }
  override getNumDaysInMonth(date: Dayjs): number {
    return date.daysInMonth();
  }
  override clone(date: Dayjs): Dayjs {
    return date.clone();
  }
  override createDate(year: number, month: number, date: number): Dayjs {
    return dayjs({year, month, day: date});
  }
  override today(): Dayjs {
    return dayjs();
  }
  override parse(value: any, parseFormat: any): Dayjs {
    return dayjs(value, parseFormat);
  }
  override format(date: Dayjs, displayFormat: any): string {
    return date.format(displayFormat);
  }
  override addCalendarYears(date: Dayjs, years: number): Dayjs {
    return date.add({years});
  }
  override addCalendarMonths(date: Dayjs, months: number): Dayjs {
    return date.add({months});
  }
  override addCalendarDays(date: Dayjs, days: number): Dayjs {
    return date.add({days});
  }
  override toIso8601(date: Dayjs): string {
    return date.format();
  }
  override isDateInstance(obj: any): boolean {
    return isDayjs(obj);
  }
  override isValid(date: Dayjs): boolean {
    return date.isValid();
  }
  override invalid(): Dayjs {
    return dayjs('Invalid Date');
  }
  override getHours(date: Dayjs): number {
    return date.hour();
  }
  override getMinutes(date: Dayjs): number {
    return date.minute();
  }
  override getSeconds(date: Dayjs): number {
    return date.second();
  }
  override addSeconds(date: Dayjs, seconds: number): Dayjs {
    return date.add({seconds});
  }
  override parseTime(value: any, parseFormat: any): Dayjs {
    return dayjs(value, parseFormat);
  }
  override setTime(target: Dayjs, hours: number, minutes: number, seconds: number): Dayjs {
    return target.set({hours, minutes, seconds});
  }
}

export const MAT_DAYJS_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'M/D/YYYY',
    timeInput: 'h:mm A',
  },
  display: {
    dateInput: 'M/D/YYYY',
    timeInput: 'h:mm A',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'MMMM D, YYYY',
    monthYearA11yLabel: 'MMMM YYYY',
    timeOptionLabel: 'h:mm A',
  },
};
